export default {
    methods: {
        productUrl({ product, variant }) {
            // console.log('products of cart', product);
            // console.log('variant.uid', variant?.uid);
            return route("products.show", {
                slug: product.slug,
                ...(variant && variant.uid ? { variant: variant.uid } : {}),
            });
        },

        productSellUrl({ product }) { // Variant is not used for selling, removed it from destructuring for clarity
            // console.log('products', product);
            return route("products.show", {
                slug: product.slug
            });
        },

        productPreorderUrl({ product, variant }) {
            // console.log('products', product);
            // console.log('variant.uid', variant?.uid);
            return route("products.show", {
                slug: product.slug,
                ...(variant && variant.uid ? { variant: variant.uid } : {}),
            });
        },

        /**
         * Checks if the item's product has a base image with a valid path.
         * The `item` parameter here is the cart item (CartItem or PreorderItem instance).
         * The `base_image` accessor is on the `product` property of the item.
         *
         * @param {Object} item The cart item (e.g., cartItem, preorderItem).
         * @returns {boolean} True if a base image path exists, false otherwise.
         */
        hasBaseImage(item) {
            // Safely check if product exists, then base_image exists, and then its path property
            return item.product && item.product.base_image && item.product.base_image.path;
        },

        /**
         * Returns the path to the item's base image, or a placeholder if not available.
         *
         * @param {Object} item The cart item.
         * @returns {string} The URL of the base image or a placeholder.
         */
        baseImage(item) {
            // Removed the unreachable `if` block, now directly using hasBaseImage for logic
            if (this.hasBaseImage(item)) {
                return item.product.base_image.path;
            }

            // Fallback placeholder image (use a generic placeholder URL)
            return `${window.FleetCart.baseUrl}/build/assets/image-placeholder.png` || 'https://placehold.co/100x100/eeeeee/333333?text=No+Image';
        },

        optionValues(option) {
            let values = [];

            // Safely iterate over option.values to get labels
            if (option && option.values) {
                for (let value of option.values) {
                    if (value && value.label) {
                        values.push(value.label);
                    }
                }
            }

            return values.join(", ");
        },

        /**
         * Determines the maximum quantity for the given item.
         *
         * @param {Object} item The cart item.
         * @returns {number|null} Max quantity or null if not managed.
         */
        maxQuantity(item) {
            // Safely access properties of item.product
            return item.product?.manage_stock && item.product?.is_in_stock ? item.product?.qty : null;
        },

        /**
         * Checks if the desired quantity exceeds the maximum stock.
         *
         * @param {Object} item The cart item.
         * @param {number} qty The desired quantity.
         * @returns {boolean} True if quantity exceeds stock and stock is managed.
         */
        exceedsMaxStock({ item, qty }) {
            // Safely access properties of item.product
            return item.product?.manage_stock && item.product?.qty < qty;
        },

        /**
         * Checks if the quantity increase button should be disabled.
         *
         * @param {Object} cartItem The cart item.
         * @returns {boolean} True if disabled, false otherwise.
         */
        isQtyIncreaseDisabled(cartItem) {
            // Safely access properties. Assuming cartItem.qty is the current quantity in the cart.
            // And cartItem.product.qty is the available stock for the product itself.
            // Check if stock is managed and if current quantity equals or exceeds available stock.
            return cartItem.product?.manage_stock && cartItem.qty >= (cartItem.product?.qty || 0);
        },

        /**
         * Handles changing the quantity of a cart item.
         * This method will likely need to dispatch to the correct store action (cart or preorder).
         *
         * @param {Object} cartItem The cart item to update.
         * @param {number} qty The new quantity.
         */
        changeQuantity(cartItem, qty) {
            qty = Number(qty); // Ensure it's a number
            if (isNaN(qty) || qty < 1) {
                qty = 1; // Default to 1 if invalid
            }

            // Determine which store module to use (cart or preorder)
            const storeModule = cartItem.product?.is_preorder ? 'preorder' : 'cart';

            // Check if the quantity exceeds max stock before updating
            if (this.exceedsMaxStock({ item: cartItem, qty })) {
                qty = cartItem.product?.qty || cartItem.qty; // Cap at available stock or current qty if stock is zero
                this.$notify(`Maximum stock available for ${cartItem.product.name} is ${qty}.`); // Notify user
            }

            // Update the quantity in the store (this assumes you have corresponding mutations/actions)
            // If `updateCart` directly calls an API, you might already have the logic there.
            // Ensure your `updateCart` method can handle both normal and preorder items correctly.
            // For now, I'm adapting based on your existing structure for `updateCart`.
            this.updateCart(cartItem, qty); // Assuming this method handles the API call and store update
        },

        /**
         * Handles updating the quantity (typically for +/- buttons).
         * This is a wrapper around `changeQuantity`.
         *
         * @param {Object} cartItem The cart item to update.
         * @param {number} qty The new quantity.
         */
        updateQuantity(cartItem, qty) {
            this.changeQuantity(cartItem, qty); // Delegate to changeQuantity for common logic
        },

        // --- Specific methods for Sell and Preorder carts if they have different update logic ---
        changeSellQuantity(sellItem, qty) {
            qty = Number(qty);
            if (isNaN(qty) || qty < 1) { qty = 1; }
            this.updateSellCart(sellItem, qty); // Assuming updateSellCart exists and handles API/store
        },

        updateSellQuantity(sellItem, qty) {
            this.changeSellQuantity(sellItem, qty);
        },

        changePreorderQuantity(preorderItem, qty) {
            qty = Number(qty);
            if (isNaN(qty) || qty < 1) { qty = 1; }
            this.updatePreorderCart(preorderItem, qty); // Assuming updatePreorderCart exists and handles API/store
        },

        updatePreorderQuantity(preorderItem, qty) {
            this.changePreorderQuantity(preorderItem, qty);
        },
    },
};
